<template>
    <section aria-label="Location Details" itemtype="http://schema.org/Restaurant" class="ion-no-padding" style="height: 90%" v-if="slug">
        <ion-toolbar mode="ios" color="white" class="modal-toolbar">
            <ion-button slot="end" @click="$emit('close')" fill="clear" color="medium" class="detail-btn" data-cy="button-close">
                <ion-icon slot="icon-only" :md="closeOutline" :ios="closeOutline" />
            </ion-button>

            <h6 class="ion-text-center bold color-black">Location Details</h6>
        </ion-toolbar>

        <ion-content>
            <ion-row v-if="showMap" class="detail-map">
                <ion-col class="pa-0">
                    <hereMap class="map" />
                </ion-col>
            </ion-row>

            <ion-row v-if="detail" style="margin-bottom: 100px">
                <ion-col size="12" class="px-0">
                    <locationLineItem :storeLocation="detail" detail />
                </ion-col>
                <ion-col class="ion-text-center" size="12">
                    <orderType :storeLocation="detail" />
                </ion-col>
            </ion-row>
        </ion-content>

        <ion-toolbar class="ion-no-margin force-bottom full-width buttons-toolbar" style="position: absolute">
            <ion-button
                v-if="!detail?.isTempClosed"
                color="primary"
                @click="selectPickupLocation(detail)"
                expand="full"
                class="ion-no-margin full-width"
                size="large"
                :disabled="!detail?.id"
                data-cy="button-pickup"
                id="button-pickup"
            >
                {{ isRedRobinStore ? 'Order At Red Robin' : 'Pick up here' }}
            </ion-button>
            <ion-button
                v-if="!detail?.isTempClosed && !isRedRobinStore"
                color="primary"
                fill="clear"
                @click="switchToDelivery()"
                expand="full"
                class="ion-no-margin full-width"
                size="large"
                :disabled="!detail?.id"
            >
                {{ isRedRobinStore ? 'Order At Red Robin' : 'Order Delivery' }}
            </ion-button>
        </ion-toolbar>
    </section>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onBeforeUnmount, watch } from 'vue';

import { IonToolbar, IonRow, IonCol, IonIcon, IonButton, IonContent } from '@ionic/vue';
import { fetchLocationDetail } from '@/services/locationService';
import locationLineItem from '@/components/locationLineItem.vue';
import orderType from '@/components/orderType.vue';
import { useStore } from 'vuex';
import { closeOutline } from 'ionicons/icons';
import updateMeta from '@/services/pageService';
import hereMap from '@/components/hereMap.vue';
import { useRoute, useRouter } from 'vue-router';
import { errorToast } from '@/services/pageService';

export default defineComponent({
    props: { slug: String, showMap: Boolean },
    components: { IonToolbar, IonRow, IonCol, IonIcon, IonButton, IonContent, locationLineItem, orderType, hereMap },
    setup(props, { emit }) {
        let detail = ref();
        const store = useStore();
        const route = useRoute();
        const router = useRouter();
        const isRedRobinStore = ref(false);

        const locationDetailSetup = (location) => {
            detail.value = location;
            store.dispatch('mapHighlight', location.id);

            updateMeta({
                title: location.name,
                description: `${location.address.address1} ${location.address.city}, ${location.address.state} ${location.address.zip}`,
            });
            isRedRobinStore.value = location.storeStatus.isRedRobinStore;
        };

        const switchToDelivery = () => {
            emit('close');
            emit('changeDelivery');
        };

        const cachedLocation = computed(() => store.getters.getNearLocations.find((l) => l.slug === props.slug));
        const selectPickupLocation = (location) => {
            if (location.storeStatus.isRedRobinStore) {
                router.push({
                    name: 'redRobin',
                    query: {
                        name: location.name,
                        slug: location.slug,
                        address1: location.address.address1,
                        address2: location.address.address2,
                        city: location.address.city,
                        state: location.address.state,
                        zip: location.address.zip,
                        phone: location.phone,
                    },
                });
                return;
            }

            store
                .dispatch('selectPickupLocation', location.id)
                .then(() => emit('close', route.query?.redirect ?? '/menu'))
                .catch((error) => errorToast(error));
        };

        const refresh = () => {
            if (!cachedLocation.value) {
                fetchLocationDetail(props.slug).then((location) => locationDetailSetup(location));
            } else {
                locationDetailSetup(cachedLocation.value);
            }
        };
        watch(
            () => props.slug,
            () => refresh()
        );
        refresh();
        onBeforeUnmount(() => store.dispatch('mapHighlight', undefined));
        return { detail, selectPickupLocation, closeOutline, isRedRobinStore, switchToDelivery };
    },
});
</script>

<style scoped>
.map {
    min-height: 30vh;
    max-height: 30vh;
}

.detail-btn {
    position: absolute;
    right: 0;
}

@media screen and (max-width: 960px) {
    .detail-map {
        margin-bottom: 16px;
    }
}

.modal-toolbar {
    padding-top: calc(var(--ion-statusbar-padding) + 5px);
}

.buttons-toolbar {
    padding: 0px;
    --padding-end: 0px;
    --padding-start: 0px;
    --padding-bottom: 0px;
    --padding-top: 0px;
}
</style>
